import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatPeriod } from "@/lib/format";
import { Badge } from "@/components/ui/Badge";
import { BillingPeriod, CollectionStatus } from "@/types/billing";

const STAGE_LABEL: Record<string, string> = {
  collecting: "Inkasowanie",
  invoicing:  "Fakturowanie",
  notifying:  "Wysyłka",
  closed:     "Zamknięty",
};
const STAGE_TONE: Record<string, "muted" | "info" | "warning" | "success"> = {
  collecting: "muted",
  invoicing:  "info",
  notifying:  "warning",
  closed:     "success",
};

const UTILITY_LABELS: Record<string, string> = {
  "en-el": "Prąd", "gaz": "Gaz", "zimna-woda": "Zimna woda",
  "podgrzanie-cw": "Ciepła woda", "co": "CO", "smieci": "Śmieci",
  "internet": "Internet", "oplata-stała-co-cw": "Opłata CO/CW",
  "settlement": "Rozl. CO",
};
const STATUS_LABEL: Record<string, string> = {
  missing: "Brak", collected: "✓", estimated: "~", optional: "—", "n/a": "N/D",
};
const STATUS_TONE: Record<string, string> = {
  missing: "text-warning", collected: "text-success", estimated: "text-warning",
  optional: "text-text-subtle", "n/a": "text-text-subtle",
};

interface Props { period?: BillingPeriod; sentCount?: number; totalCount?: number; }

const REQUIRED_UTILITIES = ["en-el", "gaz", "zimna-woda", "co", "smieci", "internet"];

function locationGlyph(cells: Record<string, { status: string }>) {
  const required = REQUIRED_UTILITIES.filter((u) => cells[u] && cells[u].status !== "n/a");
  const collected = required.filter((u) => ["collected", "estimated"].includes(cells[u].status));
  if (collected.length === 0) return "○";
  if (collected.length === required.length) return "●";
  return "◐";
}

// Collapsed bar — mobile shows only period + badge, desktop shows full summary
function CollapsedSummary({ period, sentCount, totalCount }: Props) {
  if (!period) return <span className="text-text-muted text-xs">Ładowanie…</span>;
  const status = period.collection_status;

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-text font-medium text-xs shrink-0">{formatPeriod(period.period_id)}</span>
      <Badge tone={STAGE_TONE[period.stage] ?? "muted"} className="shrink-0">
        {STAGE_LABEL[period.stage] ?? period.stage}
      </Badge>

      {/* Desktop only: per-location glyphs + invoice count */}
      <div className="hidden lg:flex items-center gap-2 text-xs text-text-muted">
        {Object.entries(status).map(([loc, cells]) => (
          <span key={loc} className="flex items-center gap-0.5">
            <span className="capitalize">{loc}</span>
            <span>{locationGlyph(cells as Record<string, { status: string }>)}</span>
          </span>
        ))}
        {totalCount !== undefined && (
          <span className="text-text-subtle">{sentCount ?? 0}/{totalCount} wys.</span>
        )}
      </div>
    </div>
  );
}

function ExpandedDesktop({ status }: { status: CollectionStatus }) {
  const cols = ["en-el", "gaz", "zimna-woda", "co", "smieci", "settlement"];
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-text-muted border-b border-border">
          <th className="text-left font-medium px-2 py-1.5">Lokal</th>
          {cols.map((c) => <th key={c} className="text-left font-medium px-2 py-1.5">{UTILITY_LABELS[c] ?? c}</th>)}
        </tr>
      </thead>
      <tbody>
        {Object.entries(status).map(([loc, cells]) => (
          <tr key={loc} className="border-t border-border">
            <td className="px-2 py-1.5 text-text capitalize font-medium">{loc}</td>
            {cols.map((c) => {
              const cell = (cells as Record<string, { status: string; collected_at?: string | null }>)[c];
              const s = cell?.status ?? "n/a";
              if (!cell || s === "n/a") return <td key={c} className="px-2 py-1.5 text-text-subtle">—</td>;
              const date = cell.collected_at?.slice(5, 10).replace("-", ".");
              return (
                <td key={c} className="px-2 py-1.5">
                  <span className={STATUS_TONE[s] ?? "text-text"}>{STATUS_LABEL[s] ?? s}</span>
                  {date && <span className="ml-1 font-mono text-text-subtle">{date}</span>}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ExpandedMobile({ status }: { status: CollectionStatus }) {
  return (
    <div className="flex flex-col gap-3">
      {Object.entries(status).map(([loc, cells]) => (
        <div key={loc}>
          <div className="text-xs font-semibold text-text uppercase tracking-wide capitalize mb-1.5">{loc}</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {Object.entries(cells as Record<string, { status: string; collected_at?: string | null }>)
              .filter(([, cell]) => cell.status !== "n/a")
              .map(([util, cell]) => (
                <div key={util} className="flex items-center justify-between gap-2">
                  <span className="text-text-muted truncate">{UTILITY_LABELS[util] ?? util}</span>
                  <span className={cn("shrink-0 font-medium", STATUS_TONE[cell.status] ?? "text-text")}>
                    {STATUS_LABEL[cell.status] ?? cell.status}
                  </span>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function BillingSummaryBar({ period, sentCount, totalCount }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-1.5 rounded hover:bg-bg-subtle transition-colors duration-150 min-w-0"
      >
        <CollapsedSummary period={period} sentCount={sentCount} totalCount={totalCount} />
        <ChevronDown className={cn("size-3.5 text-text-muted shrink-0 ml-1 transition-transform duration-200", open && "rotate-180")} />
      </button>

      {open && period && (
        <div className={cn(
          "absolute z-40 bg-bg-elevated border border-border rounded-lg shadow-md p-3",
          // Mobile: full viewport width anchored to left edge of screen
          "left-0 right-0 lg:left-0 lg:right-0",
          "top-full mt-1",
        )}>
          <div className="hidden lg:block"><ExpandedDesktop status={period.collection_status} /></div>
          <div className="lg:hidden"><ExpandedMobile status={period.collection_status} /></div>
        </div>
      )}
    </div>
  );
}
